// src/pages/auth/SignupPage.tsx
import React, { useState, useEffect } from 'react';
import { User, Mail, Smartphone, Building, Lock, CheckCircle, Search, ArrowRight } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';

interface SignupPageProps {
  onShowLogin: () => void;
  initialUserType: 'buyer' | 'seller';
}

const SignupPage: React.FC<SignupPageProps> = ({ onShowLogin, initialUserType }) => {
  const [userType, setUserType] = useState(initialUserType);
  const [step, setStep] = useState(1); // 1: GST (Seller Only), 2: Form
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Data States
  const [gstNo, setGstNo] = useState('');
  const [verifiedData, setVerifiedData] = useState<any>(null); // Stores fetched Company Data
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
  });

  const [verificationCode, setVerificationCode] = useState('');
  const [showVerificationInput, setShowVerificationInput] = useState(false);

  useEffect(() => { setUserType(initialUserType); setStep(1); }, [initialUserType]);

  // --- GST VERIFICATION HANDLER ---
  const handleVerifyGST = async () => {
    if(gstNo.length !== 15) {
        setMessage({type: 'error', text: 'Enter valid 15-digit GSTIN'});
        return;
    }
    setLoading(true);
    setMessage(null);
    try {
        const res = await fetch(`/springboot/api/auth/verify-gst/${gstNo}`);
        const data = await res.json();
        if(res.ok) {
            setVerifiedData(data);
            setMessage(null);
            setStep(2); // Go to next step
        } else {
            setMessage({type: 'error', text: 'Invalid GST Number'});
        }
    } catch(e) { setMessage({type: 'error', text: 'Verification Failed'}); }
    finally { setLoading(false); }
  };

  // --- REGISTRATION HANDLER ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
        setMessage({ type: 'error', text: "Passwords don't match" });
        return;
    }
    
    setLoading(true);
    try {
        let apiUrl = `/springboot/api/${userType === 'buyer' ? 'buyers' : 'sellers'}/register`;
        
        let payload: any = { ...formData };
        
        // Attach Verified Data for Sellers
        if (userType === 'seller' && verifiedData) {
            payload.companyName = verifiedData.companyName;
            payload.gstNumber = gstNo;
            payload.addressLine1 = verifiedData.address;
            payload.city = verifiedData.city;
            payload.state = verifiedData.state;
            payload.postalCode = verifiedData.postalCode;
        }

        const res = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });

        if (res.ok) {
            setMessage({ type: 'success', text: 'Registered! Check email for OTP.' });
            setShowVerificationInput(true);
        } else {
            setMessage({ type: 'error', text: await res.text() });
        }
    } catch (error) { setMessage({ type: 'error', text: 'Network Error' }); }
    finally { setLoading(false); }
  };

  // --- OTP VERIFY HANDLER ---
  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch('/springboot/api/auth/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, token: verificationCode, userType }),
      });
      if (res.ok) {
        setMessage({ type: 'success', text: 'Verified! Redirecting...' });
        setTimeout(onShowLogin, 1500);
      } else {
        setMessage({ type: 'error', text: 'Invalid Code' });
      }
    } catch(e) { setMessage({ type: 'error', text: 'Error' }); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex flex-col items-center justify-center h-full p-4">
      <div className="w-full max-w-sm">
        <h2 className="text-2xl font-bold mb-2 text-center text-gray-800">
             {userType === 'seller' ? 'Seller Registration' : 'Buyer Registration'}
        </h2>
        <p className="text-center text-gray-500 mb-6 text-sm">Join B2Bharat today.</p>

        {message && (
          <div className={`p-3 mb-4 rounded-md text-center text-sm ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {message.text}
          </div>
        )}

        {/* --- STEP 1: GST VERIFICATION (SELLER ONLY) --- */}
        {userType === 'seller' && step === 1 && !showVerificationInput && (
            <div className="space-y-4 animate-fade-in">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                    <p className="text-sm text-blue-800 font-semibold mb-2">Enter GSTIN to verify business</p>
                    <div className="flex gap-2">
                        <input 
                            type="text" 
                            className="flex-1 border p-2 rounded uppercase font-mono"
                            placeholder="29ABCDE1234F1Z5"
                            maxLength={15}
                            value={gstNo}
                            onChange={(e)=>setGstNo(e.target.value.toUpperCase())}
                        />
                        <button onClick={handleVerifyGST} disabled={loading} className="bg-blue-900 text-white px-3 rounded hover:bg-blue-800">
                            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <Search size={18} />}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Try: <b>29ABCDE1234F1Z5</b> for demo</p>
                </div>
                <div className="text-center">
                    <button onClick={onShowLogin} className="text-sm text-gray-600 hover:text-blue-600">Already verified? Login</button>
                </div>
            </div>
        )}

        {/* --- STEP 2: REGISTRATION FORM --- */}
        {((userType === 'buyer') || (userType === 'seller' && step === 2)) && !showVerificationInput && (
          <form onSubmit={handleSubmit} className="space-y-4 animate-fade-in-up">
            
            {/* Show Verified Company Info (Seller Only) */}
            {userType === 'seller' && verifiedData && (
                <div className="bg-green-50 p-3 rounded-lg border border-green-200 flex items-start gap-3 mb-2">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-1" />
                    <div>
                        <p className="text-xs font-bold text-green-800 uppercase">Verified Business</p>
                        <p className="font-bold text-gray-900 leading-tight">{verifiedData.companyName}</p>
                        <p className="text-xs text-gray-600">{verifiedData.address}, {verifiedData.city}</p>
                        <button type="button" onClick={()=>setStep(1)} className="text-[10px] text-red-500 underline mt-1">Change GST</button>
                    </div>
                </div>
            )}

            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400"><User size={18} /></span>
              <input type="text" placeholder="Your Name" value={formData.username} onChange={e=>setFormData({...formData, username:e.target.value})} className="w-full pl-10 p-2 border-b focus:outline-none focus:border-purple-500" required />
            </div>

            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400"><Mail size={18} /></span>
              <input type="email" placeholder="Email Address" value={formData.email} onChange={e=>setFormData({...formData, email:e.target.value})} className="w-full pl-10 p-2 border-b focus:outline-none focus:border-purple-500" required />
            </div>

            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400"><Smartphone size={18} /></span>
              <input type="text" placeholder="Mobile Number" maxLength={10} value={formData.mobile} onChange={e=>setFormData({...formData, mobile:e.target.value})} className="w-full pl-10 p-2 border-b focus:outline-none focus:border-purple-500" required />
            </div>

            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400"><Lock size={18} /></span>
              <input type="password" placeholder="Password" value={formData.password} onChange={e=>setFormData({...formData, password:e.target.value})} className="w-full pl-10 p-2 border-b focus:outline-none focus:border-purple-500" required />
            </div>

            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400"><Lock size={18} /></span>
              <input type="password" placeholder="Confirm Password" value={formData.confirmPassword} onChange={e=>setFormData({...formData, confirmPassword:e.target.value})} className="w-full pl-10 p-2 border-b focus:outline-none focus:border-purple-500" required />
            </div>

            <button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-bold py-2 px-4 rounded-full hover:shadow-lg transition disabled:opacity-50">
              {loading ? 'Creating Account...' : 'Complete Registration'}
            </button>

            <div className="text-center text-sm">
                <a href="#" onClick={(e)=>{e.preventDefault(); onShowLogin()}} className="text-purple-600 font-bold">Back to Login</a>
            </div>
          </form>
        )}

        {/* --- STEP 3: OTP VERIFICATION --- */}
        {showVerificationInput && (
           <form onSubmit={handleVerifyOTP} className="space-y-4 animate-fade-in">
               <p className="text-center text-gray-600">Enter code sent to {formData.email}</p>
               <input type="text" placeholder="Verification Code" value={verificationCode} onChange={e=>setVerificationCode(e.target.value)} className="w-full p-2 border-b text-center text-xl tracking-widest focus:outline-none focus:border-purple-500" required />
               <button type="submit" disabled={loading} className="w-full bg-green-600 text-white font-bold py-2 rounded-full hover:bg-green-700">
                   {loading ? 'Verifying...' : 'Verify Email'}
               </button>
           </form>
        )}

      </div>
    </div>
  );
};

export default SignupPage;